import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import tw from "tailwind-styled-components";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import NotFoundModel from "../../Components/NotFoundModel";
import SearchFilterBox from "../../Components/SearchFilterBox";
import { IoFilter } from "react-icons/io5";
import Table from "../../Components/Inventory/StockTransfers/Table";
import AddStockTransfer from "../../Components/Inventory/StockTransfers/AddStockTransfer";
import SideModal from "../../Components/SideModal";
import FilterModal from "../../Components/Inventory/StockTransfers/FilterModal";
import DateRange from "../../Components/DateRange";
import { Form, Formik } from "formik";
import ViewStockTransferDetails from "../../Components/Inventory/StockTransfers/ViewStockTransferDetails";
import SummaryCards from "../../Components/Inventory/StockTransfers/SummaryCards";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";

const StockTransfers = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const user = useSelector((state) => state.UserReducer.user);
    const [isFocused, setIsFocused] = useState(false);
    const [isShowAddTransfer, setIsShowAddTransfer] = useState(false);
    const [showTransferDetails, setShowTransferDetails] = useState(false);
    const [selectedTransfer, setSelectedTransfer] = useState(null);
    const [apiData, setApiData] = useState([]);
    const [kitchenList, setKitchenList] = useState([]);
    const [inventoryStocks, setInventoryStocks] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [kitchenFilter, setKitchenFilter] = useState("");
    const [startDate, setStartDate] = useState(
        moment().subtract(7, "days").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        moment(new Date()).format("YYYY-MM-DD")
    );
    const [previousStartDate, setPreviousStartDate] = useState(
        moment().subtract(7, "days").format("YYYY-MM-DD")
    );
    const [previousEndDate, setPreviousEndDate] = useState(
        moment(new Date()).format("YYYY-MM-DD")
    );
    const [filterValue, setFilterValue] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState({
        value: "",
        label: "",
        title: "",
    });
    const [summaryData, setSummaryData] = useState();
    const searchOptions = [
        { label: "Item", value: 1 },
        // { label: "Kitchen", value: 2 },
    ];

    const panelRef = useRef(null);
    const btnRef = useRef(null);

    // Parse filter value
    const parseFilterValue = (filterValue) => {
        if (!filterValue) return null;
        try {
            return JSON.parse(filterValue);
        } catch (error) {
            return filterValue;
        }
    };

    // Format filter for display
    const formatFilterForDisplay = useCallback((parsedFilter) => {
        if (!parsedFilter) return null;

        let count = 0;
        let title = "";

        if (parsedFilter.categories) {
            count += parsedFilter.categories.length || 0;
            title += `${parsedFilter.categories.length || 0} categories`;
        }

        if (parsedFilter.kitchens) {
            count += parsedFilter.kitchens.length || 0;
            if (title) title += ", ";
            title += `${parsedFilter.kitchens.length || 0} kitchens`;
        }

        if (count === 0) return null;

        return {
            value: JSON.stringify(parsedFilter),
            label: `Filter (${count})`,
            title: `Filters: ${title}`
        };
    }, []);

    // Get date range display text
    const getDateRangeDisplayText = () => {
        if (
            startDate &&
            endDate &&
            moment(startDate).isValid() &&
            moment(endDate).isValid()
        ) {
            return (
                moment(startDate).format("DD MMM yyyy") +
                " - " +
                moment(endDate).format("DD MMM yyyy")
            );
        }
        return "Select Date Range";
    };

    // Fetch stock transfers
    const fetchFunction = async () => {
        const params = new URLSearchParams();

        if (categoryFilter) params.append("categoryFilters", categoryFilter);
        if (kitchenFilter) params.append("kitchenFilters", kitchenFilter);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (searchQuery) {
            params.append("s", searchQuery);
            if (filterType) {
                params.append("t", filterType);
            }
        }

        const response = await axios.get(
            `${Config.apiUrl}/kitchen/inventory/distribution/list?${params.toString()}`,
            {
                headers: {
                    authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            toast.error(res.msg || "Failed to fetch stock transfers");
        } else {
            const distributions = res.inventoryDistributions || [];
            setKitchenList(res.kitchens || []);
            setInventoryStocks(res.inventoryStocks || []);
            setApiData(distributions);
            setSummaryData(res.transferSummary);
            setIsFocused(true);
        }
    };

    const getError = () => {
        navigate("/no-access");
    };

    const {
        isLoading: isFetching,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            getListMutate();
        }, 200);
        return () => clearTimeout(handler);
    }, [categoryFilter, kitchenFilter, searchQuery, filterType, startDate, endDate]);

    // Handle search
    const handleFilterChange = (values) => {
        setSearchQuery(values.searchQuery || "");
        setFilterType(values.filterType || "");
    };

    // Handle date range submit
    const submitHandler = (values) => {
        setFilterType("");
        setSearchQuery("");
        const newValues = {
            startDate: moment(values.startDate).format("YYYY-MM-DD"),
            endDate: moment(values.endDate).format("YYYY-MM-DD"),
        };
        setStartDate(newValues.startDate);
        setEndDate(newValues.endDate);
        setPreviousStartDate(newValues.startDate);
        setPreviousEndDate(newValues.endDate);
    };

    // Handle apply filter from FilterModal
    const onApplyFilter = (selectedFilters) => {
        // Extract category IDs from selected filters
        const categoryIds = selectedFilters.categories?.map(cat => cat.value).join(',') || "";
        const kitchenIds = selectedFilters.kitchens?.map(kitchen => kitchen.value).join(',') || "";

        // Set filters for API
        setCategoryFilter(categoryIds);
        setKitchenFilter(kitchenIds);

        // Update filter value for display
        setFilterValue(JSON.stringify(selectedFilters));

        // Update active filter display
        const formattedFilter = formatFilterForDisplay(selectedFilters);
        if (formattedFilter) {
            setActiveFilter(formattedFilter);
        } else {
            setActiveFilter({ value: "", label: "", title: "" });
        }

        // Close modal
        setIsFilterOpen(false);
    };

    // Handle filter close
    const handleFilterClose = () => {
        setIsFilterOpen(false);
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchQuery("");
        setCategoryFilter("");
        setKitchenFilter("");
        setFilterType("");
        setFilterValue("");
        setActiveFilter({ value: "", label: "", title: "" });
        setIsFilterOpen(false);
        setStartDate(moment().subtract(7, "days").format("YYYY-MM-DD"));
        setEndDate(moment(new Date()).format("YYYY-MM-DD"));
        setPreviousStartDate(moment().subtract(7, "days").format("YYYY-MM-DD"));
        setPreviousEndDate(moment(new Date()).format("YYYY-MM-DD"));
    };
    const handleAddTransferClose = () => {
        setIsShowAddTransfer(false);
    };

    //------- Add Transfer -------
    const addTransferFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/kitchen/inventory/assign`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/json",
            },
        });
    };

    const onAddTransferSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            getListMutate();
            setIsShowAddTransfer(false);
        } else {
            //   setIsShowAddTransfer(false);
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onAddTransferError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: addTransferLoading, mutate: addTransferMutate } = useMutation(
        addTransferFunction,
        {
            onSuccess: onAddTransferSuccess,
            onError: onAddTransferError,
        }
    );

    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Transfers
                    <Underline />
                </BoxTitle>
                <Filters
                    setIsShowAddTransfer={setIsShowAddTransfer}
                />
            </BoxHead>
            {(isFetching || isRefetching) && !isFocused && (
                <SummarySkeletonLoading count={4} />
            )}
            <SideModal
                isOpen={isShowAddTransfer}
                onClose={() => setIsShowAddTransfer(false)}
            >
                <AddStockTransfer
                    handleClose={handleAddTransferClose}
                    kitchenList={kitchenList}
                    inventoryStocks={inventoryStocks}
                    addTransferMutate={addTransferMutate}
                    addTransferLoading={addTransferLoading}
                />
            </SideModal>
            <SideModal
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
            >
                <FilterModal
                    onApplyFilter={onApplyFilter}
                    handleClose={handleFilterClose}
                    currentFilter={parseFilterValue(filterValue)}
                    handleClearFilters={handleClearFilters}
                    kitchenList={kitchenList}
                />
            </SideModal>

            <SideModal
                isOpen={showTransferDetails}
                onClose={() => setShowTransferDetails(false)}
            >
                <ViewStockTransferDetails
                    setShowTransferDetails={setShowTransferDetails}
                    selectedData={selectedTransfer}
                />
            </SideModal>
            {isFocused && summaryData && (
                <>
                    <SummaryCards summaryData={summaryData} />
                    <FilterTableWrapper>
                        <FilterWrapperWithSearch>
                            <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                                <SearchFilterBox
                                    options={searchOptions}
                                    handleFilterChange={handleFilterChange}
                                    handleClearFilters={handleClearFilters}
                                    className="flex-1 min-w-0"
                                />
                                <DateRange
                                    submitHandler={submitHandler}
                                    defaultLabel={getDateRangeDisplayText()}
                                    className="flex-1 min-w-0"
                                />
                            </div>
                            <div className="flex gap-2 justify-end items-center">
                                <div className="relative">
                                    <FilterBtn
                                        ref={btnRef}
                                        type="button"
                                        title={activeFilter.title || "Open Filter"}
                                        onClick={() => setIsFilterOpen((v) => !v)}
                                    >
                                        <IoFilter size={14} color="#9CA3AF" />
                                        {activeFilter?.value && (
                                            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-yellow-500 border border-white" />
                                        )}
                                    </FilterBtn>
                                </div>
                            </div>
                        </FilterWrapperWithSearch>

                        {!error && !isFetching && data && (
                            <TableWrapper>
                                <Table
                                    apiData={error ? [] : apiData}
                                    setShowTransferDetails={setShowTransferDetails}
                                    setSelectedTransfer={setSelectedTransfer}
                                />
                                {(error || apiData.length === 0) && !isFetching && (
                                    <NotFoundModel msg={"No stock transfers found."} />
                                )}
                            </TableWrapper>
                        )}
                    </FilterTableWrapper>
                </>
            )}
            {(isFetching || isRefetching) && <Loading />}
        </BoxContainer>
    );
};
const Filters = ({
    setIsShowAddTransfer,
}) => {
    return (
        <Formik >
            <Form>
                <FilterContainer>
                    <AddBtn
                        type="button"
                        onClick={() => {
                            setIsShowAddTransfer(true);
                        }}
                    >
                        {"Transfer Stock"}
                    </AddBtn>
                </FilterContainer>
            </Form>
        </Formik>
    );
};
const TableWrapper = tw.h1`mt-4 bg-white rounded-md`;
const FilterBtn = tw.button`flex items-center border whitespace-nowrap border-gray-300 justify-center rounded-md px-4 cursor-pointer bg-white hover:bg-gray-200 h-10 text-sm ml-auto`;
const AddBtn = tw.button`${(p) =>
    p.$active
        ? "bg-gray-100"
        : ""} border whitespace-nowrap border-gray-200 flex space-x-1.5 justify-center items-center text-white h-full rounded-md px-6  cursor-pointer bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 py-2.5 text-sm`;
const FilterContainer = tw.div`grid grid-cols-2 md:grid-cols-3 w-full gap-2 lg:gap-0 md:flex md:space-x-4 items-end mt-0 md:mt-0`;
export default StockTransfers;